import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { refreshClinicCache } from "@/hooks/useClinic";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";

const FIELDS = [
  { key: "name", label: "Clinic Name", testid: "clinic-name-input" },
  { key: "phone", label: "Phone (shown on receipts & website)", testid: "clinic-phone-input" },
  { key: "address", label: "Address", testid: "clinic-address-input" },
  { key: "email", label: "Email (optional)", testid: "clinic-email-input" },
  { key: "footer_note", label: "Receipt footer note", testid: "clinic-footer-input" },
];

export default function ClinicSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/public/clinic").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e))); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/clinic", form);
      refreshClinicCache(r.data);
      toast.success("Clinic details saved");
    } catch (e) { toast.error(apiError(e, "Could not save")); } finally { setSaving(false); }
  };

  if (!form) return <div className="text-slate-400 text-sm">Loading…</div>;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl space-y-5" data-testid="clinic-settings">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"><Building2 className="h-5 w-5" /></div>
        <div>
          <h3 className="font-head font-bold text-lg text-slate-900">Clinic Details</h3>
          <p className="text-sm text-slate-500">Printed on every receipt and shown on the public website.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map(({ key, label, testid }) => (
          <div key={key} className={`space-y-2 ${key === "address" || key === "footer_note" ? "sm:col-span-2" : ""}`}>
            <Label>{label}</Label>
            <Input data-testid={testid} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="clinic-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Details"}
      </Button>
    </div>
  );
}
